import React, { createContext, useContext } from 'react';
import { Box, Typography } from '@mui/material';
import useFesteVertragsleisten, { FesteVertragsleisten } from '../layout/useFesteVertragsleisten';

const LeistenKontext = createContext<FesteVertragsleisten | null>(null);

export const useVertragLeisten = () => useContext(LeistenKontext);

export default function VertragLeistenRahmen({ children }: { children: React.ReactNode }) {
  const leisten = useFesteVertragsleisten();
  return (
    <LeistenKontext.Provider value={leisten}>
      {/* Deckende Unterlage nur in der Vertragsansicht, unter App-Kopf und horizontalem Menue. Das
          Menue liegt auf zIndex.appBar und steht im DOM davor; gleiche Ebene wuerde es verdecken. */}
      <Box
        data-testid="vertrag-app-kopf-hintergrund"
        sx={{
          position: 'fixed',
          top: 0,
          left: leisten.appLinks,
          width: leisten.appBreite,
          height: leisten.appAbstand,
          bgcolor: 'background.default',
          zIndex: (theme) => theme.zIndex.appBar - 1,
          pointerEvents: 'none',
        }}
      />
      {children}
    </LeistenKontext.Provider>
  );
}

interface TitelProps {
  titel: string;
  status?: React.ReactNode;
  aktionen: React.ReactNode;
  /** RITA-1599: feste Hinweisleiste unter der Titelzeile (orange, solange der gelbe Kasten außer Sicht ist). */
  hinweis?: React.ReactNode;
  fehler?: React.ReactNode;
}

export function VertragTitelzeile({ titel, status, aktionen, hinweis, fehler }: TitelProps) {
  const leisten = useVertragLeisten();
  return (
    <Box
      ref={leisten?.kopfRef}
      data-testid="vertragsvorlage-kopfzeile"
      sx={{
        position: 'sticky',
        top: leisten?.appAbstand,
        zIndex: 11,
        bgcolor: 'background.default',
        maxHeight: leisten?.maxLeistenHoehe,
        overflowY: 'auto',
        // Spalte, damit die Hinweisleiste in schmalen Fenstern per `order` über den Titel rücken kann (siehe unten).
        display: 'flex',
        flexDirection: 'column',
        mb: 2,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
          <Typography component="h1" variant="h6" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
            {titel}
          </Typography>
          {status}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
          {aktionen}
        </Box>
      </Box>
      {/* RITA-1599: Unter dem Titel; in schmalen Fenstern (Titel umbrochen, Titelzeile blättert innen) über dem Titel,
          damit die Leiste nicht unter den Rand der innen scrollenden Titelzeile gerät (Review-Befund 4). */}
      {hinweis && <Box sx={{ order: { xs: -1, md: 0 } }}>{hinweis}</Box>}
      {fehler}
    </Box>
  );
}
